import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { ShieldCheck, ArrowUpRight } from "lucide-react";

/**
 * Forced-change screen. Reachable only when the user must change password.
 * Behaviour requested by the operator: NO skip button.
 */
export default function ChangePasswordPage() {
  const { user, loading, refresh } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If the user has already changed their password (or is a Google user), bounce to dashboard.
    if (!loading && user && !user.must_change_password) {
      navigate("/dashboard", { replace: true });
    }
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (next1.length < 8) {
      toast.error(t(lang, "cp_min_length"));
      return;
    }
    if (next1 !== next2) {
      toast.error(t(lang, "cp_mismatch"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next1,
      });
      // Rotate token + user
      localStorage.setItem("hq_token", data.token);
      if (refresh) await refresh();
      toast.success(t(lang, "cp_success"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(lang, "cp_failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="change-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 border border-[color:var(--hq-cyan)] flex items-center justify-center">
            <ShieldCheck size={18} className="text-[color:var(--hq-cyan)]" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--hq-cyan)]">
              {t(lang, "cp_required")}
            </div>
            <h1 className="font-display font-light text-2xl tracking-tight mt-1">
              {t(lang, "cp_title")}
            </h1>
          </div>
        </div>

        <p className="text-white/60 text-sm mb-6 leading-relaxed">
          {t(lang, "cp_hint")}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
              {t(lang, "cp_current")}
            </label>
            <input
              data-testid="cp-current"
              type="password"
              required
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="hq-input"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
              {t(lang, "cp_new")}
            </label>
            <input
              data-testid="cp-new"
              type="password"
              required
              minLength={8}
              value={next1}
              onChange={(e) => setNext1(e.target.value)}
              className="hq-input"
              autoComplete="new-password"
              placeholder="min 8 chars"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
              {t(lang, "cp_confirm")}
            </label>
            <input
              data-testid="cp-confirm"
              type="password"
              required
              minLength={8}
              value={next2}
              onChange={(e) => setNext2(e.target.value)}
              className="hq-input"
              autoComplete="new-password"
            />
          </div>
          <button
            data-testid="cp-submit"
            disabled={busy}
            className="hq-btn hq-btn-primary w-full py-3 disabled:opacity-50"
          >
            {busy ? t(lang, "cp_saving") : t(lang, "cp_submit")} <ArrowUpRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
